import { useCallback } from "react";

import {
  Cell,
  Column,
  Row,
  Table
} from "@/src/components/organisms/AdminDataTable/reactTable";

const INJECTION_CHARS = new Set(["=", "+", "-", "@", "\t", "\r"]);

const stringifyValue = (raw: unknown): string => {
  if (typeof raw === "string") return raw;
  if (
    typeof raw === "number" ||
    typeof raw === "boolean" ||
    typeof raw === "bigint"
  ) {
    return String(raw);
  }
  if (raw instanceof Date) return raw.toISOString();
  return JSON.stringify(raw);
};

const sanitizeCell = (raw: unknown): string => {
  if (raw === null || raw === undefined) return "";
  const str = stringifyValue(raw);
  const prefixed = INJECTION_CHARS.has(str.charAt(0)) ? `'${str}` : str;
  const escaped = prefixed.replaceAll('"', '""');
  if (/[",\n\r]/.test(escaped)) {
    return `"${escaped}"`;
  }
  return escaped;
};

const headerText = <TData extends object>(
  column: Column<TData, unknown>
): string => {
  const header = column.columnDef.header;
  if (typeof header === "string") return header;
  const meta = column.columnDef.meta as { exportHeader?: string } | undefined;
  if (meta?.exportHeader) return meta.exportHeader;
  return column.id;
};

const cellText = <TData extends object>(cell: Cell<TData, unknown>): string => {
  const meta = cell.column.columnDef.meta as
    | { exportValue?: (row: Row<TData>) => unknown }
    | undefined;
  if (meta?.exportValue) {
    return sanitizeCell(meta.exportValue(cell.row));
  }
  return sanitizeCell(cell.getValue());
};

const buildCsv = <TData extends object>(table: Table<TData>): string => {
  const columns = table
    .getVisibleLeafColumns()
    .filter((col) => col.id !== "actions");
  const headerLine = columns.map(headerText).map(sanitizeCell).join(",");
  const rows = table.getFilteredRowModel().rows;
  const bodyLines = rows.map((row) => {
    const visibleCells = row
      .getVisibleCells()
      .filter((cell) => cell.column.id !== "actions");
    return visibleCells.map(cellText).join(",");
  });
  return [headerLine, ...bodyLines].join("\n");
};

const triggerDownload = (content: string, fileName: string) => {
  const blob = new Blob(["\uFEFF", content], {
    type: "text/csv;charset=utf-8;"
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const useCsvExport = <TData extends object>(
  table: Table<TData>,
  baseName: string
) => {
  return useCallback(() => {
    const csv = buildCsv(table);
    const date = new Date().toISOString().slice(0, 10);
    triggerDownload(csv, `${baseName}-${date}.csv`);
  }, [table, baseName]);
};
